/**
 * Centralized hook for nakshatra pada settings validation and access
 * Single source of truth for all nakshatra pada setting operations
 *
 * IMPORTANT: Pada depends on Nakshatra Chakra!
 * - Pada can only be enabled when Nakshatra Chakra is enabled
 * - Disabling Nakshatra Chakra automatically hides Pada
 */

import { devWarn, devError } from '../utils/devLog';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMultipleCharts } from '../components/common/AstrologyChart/MultipleChartsContext';
import { useDivisionalChakraSettings } from './useDivisionalChakraSettings';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';

interface NakshatraPadaSettings {
    showNakshatraPada: boolean;
    isLoading: boolean;
    error: string | null;
    updateSetting: (enabled: boolean) => Promise<void>;
    chartId: string | null;
    isDisabled: boolean;  // Depends on showNakshatraChakra
}

/** Read pada visibility from chart settings, applying cascade dependency */
const readPadaFromSettings = (
    settings: Record<string, unknown> | undefined,
    showNakshatraChakra: boolean
): boolean => {
    const value = settings?.showNakshatraPada;
    if (typeof value !== 'boolean') return false;
    // Cascade: pada off when nakshatra chakra off
    return showNakshatraChakra ? value : false;
};

/**
 * Custom hook for managing nakshatra pada settings for the ACTIVE chart only
 * Handles validation, error states, cascade dependencies on nakshatra chakra
 */
export const useNakshatraPadaSettings = (): NakshatraPadaSettings => {
    const { activeChartId, getActiveChart, updateChart, loadingCharts } = useMultipleCharts();
    const { showChakra: showNakshatraChakra } = useDivisionalChakraSettings('nakshatra');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Tracks previous emitted state; null means not yet initialized for this chart
    const previousPadaStateRef = useRef<{ chartId: string; showPada: boolean } | null>(null);

    const isDisabled = !showNakshatraChakra;

    // Get current setting with validation for ACTIVE chart only
    const getNakshatraPadaSetting = useCallback((): boolean => {
        if (!activeChartId) return false;

        const activeChart = getActiveChart();
        if (!activeChart) {
            if (!loadingCharts.has(activeChartId)) {
                devWarn(`useNakshatraPadaSettings: No active chart for ID ${activeChartId}`);
            }
            return false;
        }

        return readPadaFromSettings(
            activeChart.settings as Record<string, unknown> | undefined,
            showNakshatraChakra
        );
    }, [activeChartId, getActiveChart, showNakshatraChakra, loadingCharts]);

    const [showNakshatraPada, setShowNakshatraPada] = useState(() => {
        return activeChartId ? getNakshatraPadaSetting() : false;
    });

    // Update setting with validation and error handling
    const updateSetting = useCallback(async (enabled: boolean): Promise<void> => {
        if (enabled && !showNakshatraChakra) {
            const msg = 'Cannot enable Nakshatra Pada - Nakshatra Chakra is disabled';
            devWarn('useNakshatraPadaSettings:', msg);
            setError(msg);
            return;
        }

        if (!activeChartId || !updateChart) {
            devError('useNakshatraPadaSettings: No active chart or updateChart unavailable');
            setError('No active chart available for nakshatra pada update');
            return;
        }

        if (showNakshatraPada === enabled) return;

        setIsLoading(true);
        setError(null);

        try {
            const activeChart = getActiveChart();
            if (!activeChart) {
                throw new Error('Active chart became unavailable during update');
            }

            await updateChart(activeChartId, {
                settings: {
                    ...activeChart.settings,
                    showNakshatraPada: enabled
                }
            });

            setShowNakshatraPada(enabled);
        } catch (err) {
            const msg = `Failed to update nakshatra pada setting: ${err}`;
            devError('useNakshatraPadaSettings:', msg);
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    }, [activeChartId, updateChart, getActiveChart, showNakshatraChakra, showNakshatraPada]);

    // Sync local state when activeChartId changes or nakshatra chakra cascades
    useEffect(() => {
        if (!activeChartId) {
            setError(null);
            return;
        }

        const newSetting = getNakshatraPadaSetting();
        setShowNakshatraPada(prev => prev !== newSetting ? newSetting : prev);
        setError(null);
    }, [activeChartId, getNakshatraPadaSetting]);

    // Emit events when showNakshatraPada changes (user toggle only)
    // First load restoration handled by ChakraEventSequenceManager Rule 2
    // Cascade disable handled by ChakraEventSequenceManager Rule 1
    useEffect(() => {
        if (!activeChartId) return;

        // Block enabling pada if nakshatra chakra is off
        if (showNakshatraPada && !showNakshatraChakra) return;

        const currentState = { chartId: activeChartId, showPada: showNakshatraPada };
        const prev = previousPadaStateRef.current;

        // First run for any chart: capture state, don't emit (Rule 2 handles first load)
        if (!prev || prev.chartId !== activeChartId) {
            previousPadaStateRef.current = currentState;
            return;
        }

        // Emit only if pada state actually changed (user toggle)
        if (prev.showPada !== showNakshatraPada) {
            globalEventStream.emit({
                id: `pada-toggle-${activeChartId}-${Date.now()}`,
                type: 'nakshatra.pada.toggle',
                source: 'nakshatra-pada-settings-hook-user-toggle',
                timestamp: Date.now(),
                chartId: activeChartId,
                data: {
                    chartId: activeChartId,
                    showPada: showNakshatraPada
                }
            });
            previousPadaStateRef.current = currentState;
        }
    }, [showNakshatraPada, activeChartId, showNakshatraChakra]);

    return {
        showNakshatraPada,
        isLoading,
        error,
        updateSetting,
        chartId: activeChartId,
        isDisabled
    };
};
